<template>
  <div class="mrq-panel">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h6 class="mrq-title mb-0">Phiếu yêu cầu vật tư (MRQ)</h6>
      <b-button
        v-if="canCreate && creatable"
        size="sm"
        variant="outline-primary"
        @click="openCreate"
      >
        <i class="fas fa-plus"></i> Tạo MRQ
      </b-button>
    </div>

    <b-overlay :show="loading" rounded>
      <table class="table table-bordered mrq-table">
        <thead>
          <tr>
            <th style="width: 40px">#</th>
            <th>Số phiếu</th>
            <th>Trạng thái</th>
            <th>Ngày tạo</th>
            <th class="text-center" style="width: 220px">Thao tác</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(mrq, idx) in mrqs" :key="mrq.id">
            <td class="text-center">{{ idx + 1 }}</td>
            <td class="mrq-code">{{ mrq.code }}</td>
            <td>
              <b-badge :variant="mrqVariant(mrq.status)">{{
                mrqLabel(mrq.status)
              }}</b-badge>
            </td>
            <td>{{ formatDate(mrq.createdAt) }}</td>
            <td class="text-center">
              <b-button
                size="sm"
                variant="link"
                class="p-0 mr-2"
                title="Xem"
                @click="openDetail(mrq)"
              >
                <i class="fas fa-eye"></i>
              </b-button>
              <b-button
                v-if="canIssue && mrq.status === MRQ_STATUS.PENDING"
                size="sm"
                variant="link"
                class="p-0 mr-2 text-primary"
                title="Phát vật tư"
                @click="openIssue(mrq)"
              >
                <i class="fas fa-dolly"></i>
              </b-button>
              <b-button
                v-if="canReturn && mrq.status === MRQ_STATUS.ISSUED"
                size="sm"
                variant="link"
                class="p-0 mr-2 text-warning"
                title="Hoàn vật tư dư"
                @click="openReturn(mrq)"
              >
                <i class="fas fa-undo"></i>
              </b-button>
              <b-button
                v-if="canCreate && mrq.status === MRQ_STATUS.PENDING"
                size="sm"
                variant="link"
                class="p-0 text-danger"
                title="Huỷ phiếu (chưa phát)"
                @click="onCancel(mrq)"
              >
                <i class="fas fa-ban"></i>
              </b-button>
            </td>
          </tr>
          <tr v-if="!mrqs.length">
            <td colspan="5" class="text-center text-muted py-3">
              Chưa có phiếu yêu cầu vật tư.
            </td>
          </tr>
        </tbody>
      </table>
    </b-overlay>

    <!-- ============ Modal: Tạo MRQ ============ -->
    <b-modal ref="create-modal" title="Tạo phiếu yêu cầu vật tư" size="xl" hide-footer>
      <b-row class="mb-2">
        <b-col md="5">
          <label class="mrq-label">Kho lấy hàng <span class="text-danger">*</span></label>
          <SearchStore
            :storeId.sync="form.storeId"
            :storeName.sync="storeName"
            @onStoreSelected="onStoreSelected"
          />
        </b-col>
        <b-col md="7">
          <label class="mrq-label">Ghi chú</label>
          <b-form-input v-model="form.description" size="sm" />
        </b-col>
      </b-row>

      <div class="mrq-create-lines">
        <table class="table table-bordered mrq-line-table">
          <thead>
            <tr>
              <th style="width: 40px">#</th>
              <th style="min-width: 320px">Vật tư</th>
              <th class="text-center" style="width: 110px">Tồn khả dụng</th>
              <th class="text-center" style="width: 120px">SL yêu cầu</th>
              <th style="min-width: 160px">Ghi chú</th>
              <th style="width: 40px"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(line, idx) in form.lines" :key="idx">
              <td class="text-center align-middle">{{ idx + 1 }}</td>
              <td>
                <multiselect
                  v-model="line.product"
                  :options="productOptions"
                  :loading="productLoading"
                  :internal-search="false"
                  :disabled="!form.storeId"
                  track-by="id"
                  label="name"
                  :placeholder="form.storeId ? 'Tìm vật tư theo tên / mã' : 'Chọn kho lấy hàng trước'"
                  open-direction="bottom"
                  :show-labels="false"
                  class="mrq-product-select"
                  @search-change="onProductSearch"
                  @select="(opt) => onProductSelect(line, opt)"
                >
                  <template slot="option" slot-scope="{ option }">
                    <div class="mrq-opt">
                      <span class="mrq-opt-name">{{ option.name }}</span>
                      <span class="mrq-opt-meta">
                        <span v-if="option.code" class="mrq-opt-code">{{ option.code }}</span>
                        <span class="mrq-opt-stock">Tồn: {{ option.quantityInStock }}</span>
                      </span>
                    </div>
                  </template>
                  <span slot="noResult">Không tìm thấy vật tư phù hợp</span>
                  <span slot="noOptions">Nhập tên hoặc mã để tìm vật tư</span>
                </multiselect>
              </td>
              <td class="text-center align-middle">
                {{ line.availableStock != null ? line.availableStock : '—' }}
              </td>
              <td>
                <b-form-input
                  v-model.number="line.requestedQuantity"
                  type="number"
                  min="1"
                  size="sm"
                  class="text-center"
                />
              </td>
              <td>
                <b-form-input v-model="line.note" size="sm" />
              </td>
              <td class="text-center align-middle">
                <b-button
                  size="sm"
                  variant="link"
                  class="text-danger p-0"
                  @click="removeLine(idx)"
                >
                  <i class="fas fa-trash"></i>
                </b-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <b-button size="sm" variant="outline-secondary" @click="addLine">
        <i class="fas fa-plus"></i> Thêm dòng
      </b-button>

      <div class="mt-3 d-flex justify-content-end">
        <b-button variant="secondary" class="mr-2" @click="hide('create-modal')">Trở về</b-button>
        <b-button variant="primary" :disabled="submitting || !createValid" @click="submitCreate">
          <b-spinner v-if="submitting" small /> Tạo phiếu
        </b-button>
      </div>
    </b-modal>

    <!-- ============ Modal: Phát vật tư ============ -->
    <b-modal ref="issue-modal" :title="`Phát vật tư — ${activeMrq && activeMrq.code}`" size="lg" hide-footer>
      <div class="table-responsive">
        <table class="table table-bordered mrq-line-table">
          <thead>
            <tr>
              <th style="width: 40px">#</th>
              <th>Vật tư</th>
              <th class="text-center" style="width: 90px">Yêu cầu</th>
              <th class="text-center" style="width: 110px">Tồn khả dụng</th>
              <th class="text-center" style="width: 120px">SL phát</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(line, idx) in issueLines" :key="line.detailId">
              <td class="text-center align-middle">{{ idx + 1 }}</td>
              <td>
                {{ line.productName || line.productId }}
                <span v-if="line.productCode" class="text-muted small">— {{ line.productCode }}</span>
              </td>
              <td class="text-center align-middle">{{ line.requestedQuantity }}</td>
              <td class="text-center align-middle">{{ line.availableStock }}</td>
              <td>
                <b-form-input
                  v-model.number="line.issuedQuantity"
                  type="number"
                  min="0"
                  :max="Math.min(line.requestedQuantity, line.availableStock)"
                  size="sm"
                  class="text-center"
                  :class="{ 'is-invalid': lineIssueError(line) }"
                />
                <small v-if="lineIssueError(line)" class="text-danger">
                  {{ lineIssueError(line) }}
                </small>
                <small
                  v-else-if="issueLineShortfall(line) > 0"
                  class="text-warning d-block"
                >
                  Còn thiếu {{ issueLineShortfall(line) }} so với yêu cầu
                </small>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <b-alert :show="issueShortfall" variant="warning" class="py-2 px-3">
        Còn dòng phát thiếu so với yêu cầu — phần thiếu tạo MRQ đợt 2 khi hàng về (TR-7).
      </b-alert>
      <div class="mt-2 d-flex justify-content-end">
        <b-button variant="secondary" class="mr-2" @click="hide('issue-modal')">Trở về</b-button>
        <b-button variant="primary" :disabled="submitting || !issueValid" @click="submitIssue">
          <b-spinner v-if="submitting" small /> Xác nhận phát
        </b-button>
      </div>
    </b-modal>

    <!-- ============ Modal: Hoàn vật tư dư ============ -->
    <b-modal ref="return-modal" :title="`Hoàn vật tư — ${activeMrq && activeMrq.code}`" size="lg" hide-footer>
      <div class="table-responsive">
        <table class="table table-bordered mrq-line-table">
          <thead>
            <tr>
              <th style="width: 40px">#</th>
              <th>Vật tư</th>
              <th class="text-center" style="width: 90px">Đã phát</th>
              <th class="text-center" style="width: 90px">Đã hoàn</th>
              <th class="text-center" style="width: 130px">Hoàn lần này</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(line, idx) in returnLines" :key="line.detailId">
              <td class="text-center align-middle">{{ idx + 1 }}</td>
              <td>{{ line.productName || line.productId }}</td>
              <td class="text-center align-middle">{{ line.issuedQuantity }}</td>
              <td class="text-center align-middle">{{ line.returnedQuantity }}</td>
              <td>
                <b-form-input
                  v-model.number="line.returnNow"
                  type="number"
                  min="0"
                  :max="line.issuedQuantity - line.returnedQuantity"
                  size="sm"
                  class="text-center"
                  :class="{ 'is-invalid': lineReturnError(line) }"
                />
                <small v-if="lineReturnError(line)" class="text-danger">
                  {{ lineReturnError(line) }}
                </small>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="mt-2 d-flex justify-content-end">
        <b-button variant="secondary" class="mr-2" @click="hide('return-modal')">Trở về</b-button>
        <b-button variant="warning" :disabled="submitting || !returnValid" @click="submitReturn">
          <b-spinner v-if="submitting" small /> Xác nhận hoàn
        </b-button>
      </div>
    </b-modal>

    <!-- ============ Modal: Chi tiết MRQ ============ -->
    <b-modal ref="detail-modal" :title="`Chi tiết — ${activeMrq && activeMrq.code}`" size="lg" hide-footer>
      <table class="table table-bordered mrq-line-table">
        <thead>
          <tr>
            <th style="width: 40px">#</th>
            <th>Vật tư</th>
            <th class="text-center">Yêu cầu</th>
            <th class="text-center">Đã phát</th>
            <th class="text-center">Còn thiếu</th>
            <th class="text-center">Đã hoàn</th>
            <th class="text-right">Giá vốn</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(line, idx) in detailLines" :key="line.id">
            <td class="text-center">{{ idx + 1 }}</td>
            <td>
              {{ line.productName || line.productId }}
              <span v-if="line.productCode" class="text-muted small">— {{ line.productCode }}</span>
            </td>
            <td class="text-center">{{ line.requestedQuantity }}</td>
            <td class="text-center">{{ line.issuedQuantity }}</td>
            <td class="text-center">
              <span
                v-if="shortfall(line) > 0"
                class="text-danger font-weight-bold"
                title="Phần chưa phát — tạo MRQ đợt 2 khi hàng về (TR-7)"
                >{{ shortfall(line) }}</span
              >
              <span v-else class="text-muted">0</span>
            </td>
            <td class="text-center">{{ line.returnedQuantity }}</td>
            <td class="text-right">{{ line.unitPrice != null ? formatPrice(line.unitPrice) : '—' }}</td>
          </tr>
        </tbody>
      </table>
      <p class="text-muted small mb-0">
        Còn thiếu = Yêu cầu − Đã phát (phần chưa phát, tạo MRQ đợt 2 khi hàng về — TR-7). Giá vốn
        (read-only) — snapshot lúc phát, dùng cho quyết toán.
      </p>

      <hr />
      <h6 class="mrq-title mb-2">Lịch sử thao tác</h6>
      <b-overlay :show="historyLoading" rounded>
        <div v-if="!historyRows.length" class="text-center text-muted py-3">
          Chưa có lịch sử thao tác.
        </div>
        <ul v-else class="mrq-timeline">
          <li v-for="(h, i) in historyRows" :key="h.id || i" class="mrq-timeline-item">
            <div class="mrq-timeline-dot"></div>
            <div class="mrq-timeline-body">
              <div class="mrq-timeline-head">
                <b class="mrq-timeline-action">{{ describeHistory(h).action }}</b>
                <span class="mrq-timeline-time">{{ formatDate(h.createdAt) }}</span>
              </div>
              <div class="mrq-timeline-meta">· {{ actorName(h) }}</div>
              <div v-if="describeHistory(h).detail" class="mrq-timeline-note">
                {{ describeHistory(h).detail }}
              </div>
            </div>
          </li>
        </ul>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment';
import { get } from 'lodash';
import debounce from 'debounce';
import Multiselect from 'vue-multiselect';
import Swal from 'sweetalert2';
import productService from '@/core/services/product';
import MaterialRequisitionService from '@/core/services/materialRequisition.service';
import SearchStore from '@/view/components/stores/SearchStore.vue';
import {
  SO_STATUS,
  MRQ_STATUS,
  MRQ_STATUS_LABEL,
  MRQ_STATUS_VARIANT,
  SO_PERMISSION,
} from '@/core/constants/serviceOrder';
import { convertPrice, makeToastSuccess, makeToastFaile } from '@/utils/common';
import { checkPermissions } from '@/utils/saveDataToLocal';

const num = (v) => Number(v) || 0;

export default {
  name: 'MaterialRequisitionPanel',
  components: { Multiselect, SearchStore },
  props: {
    serviceOrderId: { type: String, required: true },
    soStatus: { type: String, default: '' },
  },
  data() {
    return {
      MRQ_STATUS,
      loading: false,
      submitting: false,
      mrqs: [],
      // Tên kho hiển thị cho SearchStore (sync 2 chiều với storeId trong form).
      storeName: '',
      productOptions: [],
      productLoading: false,
      activeMrq: null,
      form: { storeId: null, description: '', lines: [] },
      issueLines: [],
      returnLines: [],
      detailLines: [],
      historyRows: [],
      historyLoading: false,
    };
  },
  computed: {
    canCreate() {
      return checkPermissions([SO_PERMISSION.MRQ_CREATE]);
    },
    canIssue() {
      return checkPermissions([SO_PERMISSION.MRQ_ISSUE]);
    },
    canReturn() {
      return checkPermissions([SO_PERMISSION.MRQ_RETURN]);
    },
    // Tạo MRQ khi SO đang sửa chữa hoặc đang/đã xuất vật tư (TR-8 — đợt 2).
    creatable() {
      return [
        SO_STATUS.REPAIRING,
        SO_STATUS.PENDING_PARTS,
        SO_STATUS.PARTS_ISSUED,
      ].includes(this.soStatus);
    },
    createValid() {
      const lines = this.form.lines.filter((l) => l.productId);
      return (
        !!this.form.storeId &&
        lines.length > 0 &&
        lines.every((l) => num(l.requestedQuantity) >= 1)
      );
    },
    issueValid() {
      if (!this.issueLines.length) return false;
      if (this.issueLines.some((l) => this.lineIssueError(l))) return false;
      return this.issueLines.reduce((s, l) => s + num(l.issuedQuantity), 0) > 0;
    },
    issueShortfall() {
      return this.issueLines.some(
        (l) => num(l.issuedQuantity) < num(l.requestedQuantity),
      );
    },
    returnValid() {
      if (!this.returnLines.length) return false;
      if (this.returnLines.some((l) => this.lineReturnError(l))) return false;
      return this.returnLines.reduce((s, l) => s + num(l.returnNow), 0) > 0;
    },
  },
  created() {
    this.onProductSearch = debounce(this.runProductSearch, 400);
    this.load();
  },
  methods: {
    formatDate(d) {
      return d ? moment(d).format('DD/MM/YYYY HH:mm') : '—';
    },
    formatPrice(v) {
      return convertPrice(num(v)) + ' đ';
    },
    // Còn thiếu (modal Chi tiết) = Yêu cầu − Đã phát: phần chưa phát → MRQ đợt 2 (TR-7).
    shortfall(line) {
      return Math.max(0, num(line.requestedQuantity) - num(line.issuedQuantity));
    },
    // Còn thiếu dự kiến khi đang nhập SL phát (modal Phát) = Yêu cầu − SL phát đang nhập.
    issueLineShortfall(line) {
      return Math.max(0, num(line.requestedQuantity) - num(line.issuedQuantity));
    },
    mrqLabel(s) {
      return MRQ_STATUS_LABEL[s] || s;
    },
    mrqVariant(s) {
      return MRQ_STATUS_VARIANT[s] || 'secondary';
    },
    hide(ref) {
      this.$refs[ref].hide();
    },
    async load() {
      this.loading = true;
      try {
        const res = await MaterialRequisitionService.listByServiceOrder(
          this.serviceOrderId,
        );
        this.mrqs = get(res, 'data.data', []) || [];
      } catch (e) {
        makeToastFaile('Lỗi tải danh sách MRQ');
      } finally {
        this.loading = false;
      }
    },
    // ---- Tạo ----
    openCreate() {
      this.form = { storeId: null, description: '', lines: [] };
      this.storeName = '';
      this.productOptions = [];
      this.addLine();
      this.$refs['create-modal'].show();
    },
    addLine() {
      this.form.lines.push({
        product: null,
        productId: null,
        requestedQuantity: 1,
        note: '',
        availableStock: null,
      });
    },
    removeLine(idx) {
      this.form.lines.splice(idx, 1);
    },
    // SearchStore đã sync form.storeId (via :storeId.sync) — chỉ cần reset SP theo kho mới.
    onStoreSelected() {
      this.onStoreChange();
    },
    onStoreChange() {
      // Đổi kho → reset SP đã chọn (tồn theo kho khác).
      this.productOptions = [];
      this.form.lines.forEach((l) => {
        l.product = null;
        l.productId = null;
        l.availableStock = null;
      });
    },
    async runProductSearch(query) {
      const text = (query || '').trim();
      if (!this.form.storeId || text.length < 2) {
        this.productOptions = [];
        return;
      }
      this.productLoading = true;
      try {
        const res = await productService.searchProducts({
          storeId: this.form.storeId,
          productName: text,
          // Phiếu dịch vụ chỉ phát hàng theo số lượng → loại xe/hàng theo số khung/serial khỏi dropdown.
          excludeTypes: 'CHASSIS_ENGINE,SERIES',
        });
        const rows = get(res, 'data.data', []) || [];
        this.productOptions = rows.map((el) => ({
          id: el.id,
          name: el.productName || el.name,
          code: el.code || el.productCode || '',
          quantityInStock: el.productStock?.length
            ? num(el.productStock[0].inStockQuantity)
            : num(el.quantityInStock),
        }));
      } catch (e) {
        this.productOptions = [];
      } finally {
        this.productLoading = false;
      }
    },
    onProductSelect(line, opt) {
      if (!opt) return;
      line.productId = opt.id;
      line.availableStock = opt.quantityInStock;
    },
    async submitCreate() {
      const lines = this.form.lines
        .filter((l) => l.productId)
        .map((l) => ({
          productId: l.productId,
          requestedQuantity: num(l.requestedQuantity),
          note: l.note || undefined,
        }));
      this.submitting = true;
      try {
        await MaterialRequisitionService.create({
          serviceOrderId: this.serviceOrderId,
          storeId: this.form.storeId,
          description: this.form.description || undefined,
          lines,
        });
        makeToastSuccess('Tạo phiếu vật tư thành công');
        this.hide('create-modal');
        await this.load();
        this.$emit('changed');
      } catch (e) {
        makeToastFaile(this.errMsg(e, 'Tạo phiếu vật tư thất bại'));
      } finally {
        this.submitting = false;
      }
    },
    // ---- Phát ----
    async openIssue(mrq) {
      this.activeMrq = mrq;
      const detail = await this.fetchDetail(mrq.id);
      this.issueLines = (detail.details || []).map((d) => ({
        detailId: d.id,
        productId: d.productId,
        productName: d.productName,
        productCode: d.productCode,
        requestedQuantity: num(d.requestedQuantity),
        availableStock: num(d.availableStock),
        issuedQuantity: Math.min(num(d.requestedQuantity), num(d.availableStock)),
      }));
      this.$refs['issue-modal'].show();
    },
    lineIssueError(line) {
      const q = num(line.issuedQuantity);
      if (q < 0) return 'SL không hợp lệ';
      if (q > num(line.requestedQuantity)) return 'Vượt số yêu cầu';
      if (q > num(line.availableStock)) return 'Thiếu tồn (vượt tồn khả dụng)';
      return '';
    },
    async submitIssue() {
      const lines = this.issueLines.map((l) => ({
        detailId: l.detailId,
        issuedQuantity: num(l.issuedQuantity),
      }));
      this.submitting = true;
      try {
        await MaterialRequisitionService.issue(this.activeMrq.id, lines);
        makeToastSuccess('Phát vật tư thành công');
        this.hide('issue-modal');
        await this.load();
        this.$emit('changed');
      } catch (e) {
        makeToastFaile(this.errMsg(e, 'Phát vật tư thất bại'));
      } finally {
        this.submitting = false;
      }
    },
    // ---- Hoàn ----
    async openReturn(mrq) {
      this.activeMrq = mrq;
      const detail = await this.fetchDetail(mrq.id);
      this.returnLines = (detail.details || [])
        .filter((d) => num(d.issuedQuantity) - num(d.returnedQuantity) > 0)
        .map((d) => ({
          detailId: d.id,
          productId: d.productId,
          productName: d.productName,
          issuedQuantity: num(d.issuedQuantity),
          returnedQuantity: num(d.returnedQuantity),
          returnNow: 0,
        }));
      this.$refs['return-modal'].show();
    },
    lineReturnError(line) {
      const q = num(line.returnNow);
      if (q < 0) return 'SL không hợp lệ';
      if (q > line.issuedQuantity - line.returnedQuantity) return 'Vượt số còn lại';
      return '';
    },
    async submitReturn() {
      const lines = this.returnLines
        .filter((l) => num(l.returnNow) > 0)
        .map((l) => ({ detailId: l.detailId, returnedQuantity: num(l.returnNow) }));
      this.submitting = true;
      try {
        await MaterialRequisitionService.returnMaterial(this.activeMrq.id, lines);
        makeToastSuccess('Hoàn vật tư thành công');
        this.hide('return-modal');
        await this.load();
        this.$emit('changed');
      } catch (e) {
        makeToastFaile(this.errMsg(e, 'Hoàn vật tư thất bại'));
      } finally {
        this.submitting = false;
      }
    },
    // ---- Xem ----
    async openDetail(mrq) {
      this.activeMrq = mrq;
      this.historyRows = [];
      const detail = await this.fetchDetail(mrq.id);
      this.detailLines = detail.details || [];
      this.$refs['detail-modal'].show();
      this.loadMrqHistory(mrq.id);
    },
    // Tải lịch sử thao tác cấp MRQ (material_requisition_histories).
    async loadMrqHistory(id) {
      this.historyLoading = true;
      try {
        const res = await MaterialRequisitionService.getHistory(id);
        this.historyRows = get(res, 'data.data') || [];
      } catch (e) {
        this.historyRows = [];
        makeToastFaile('Lỗi tải lịch sử phiếu vật tư');
      } finally {
        this.historyLoading = false;
      }
    },
    // Tên người thực hiện (actor) của 1 bản ghi lịch sử.
    actorName(h) {
      return get(h, 'creator.fullName') || '—';
    },
    // Tra tên vật tư từ detailLines đã nạp (theo detailId hoặc productId).
    productNameByDetail(detailId) {
      const d = this.detailLines.find((x) => x.id === detailId);
      return (d && (d.productName || d.productId)) || '—';
    },
    productNameById(productId) {
      const d = this.detailLines.find((x) => x.productId === productId);
      return (d && (d.productName || d.productId)) || productId || '—';
    },
    // Dịch 1 bản ghi lịch sử (JSON newValue) → { action, detail } dễ đọc.
    // try/catch TỪNG bản ghi: 1 bản hỏng KHÔNG làm vỡ cả timeline.
    describeHistory(h) {
      let nv = {};
      try {
        nv = JSON.parse(h.newValue || '{}') || {};
      } catch (e) {
        return { action: 'Thao tác', detail: '—' };
      }
      if (Array.isArray(nv.returned)) {
        return {
          action: 'Hoàn vật tư',
          detail: nv.returned
            .map((r) => `${r.returnedQuantity} x ${this.productNameByDetail(r.detailId)}`)
            .join(', '),
        };
      }
      if (Array.isArray(nv.issued)) {
        return {
          action: 'Phát vật tư',
          detail: nv.issued
            .map((i) => `${i.issuedQuantity} x ${this.productNameByDetail(i.detailId)}`)
            .join(', '),
        };
      }
      if (Array.isArray(nv.lines)) {
        return {
          action: 'Tạo phiếu',
          detail: nv.lines
            .map((l) => `${l.requestedQuantity} x ${this.productNameById(l.productId)}`)
            .join(', '),
        };
      }
      if (nv.status === MRQ_STATUS.CANCELLED) {
        return { action: 'Huỷ phiếu', detail: nv.reason || '' };
      }
      return { action: this.mrqLabel(nv.status) || 'Thao tác', detail: '' };
    },
    // ---- Huỷ thủ công ----
    async onCancel(mrq) {
      const ok = await Swal.fire({
        title: 'Huỷ phiếu vật tư?',
        text: `Phiếu ${mrq.code} đang chờ phát sẽ bị huỷ.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Huỷ phiếu',
        cancelButtonText: 'Trở về',
      });
      if (!ok || !ok.isConfirmed) return;
      try {
        await MaterialRequisitionService.cancel(mrq.id);
        makeToastSuccess('Huỷ phiếu vật tư thành công');
        await this.load();
        this.$emit('changed');
      } catch (e) {
        makeToastFaile(this.errMsg(e, 'Huỷ phiếu vật tư thất bại'));
      }
    },
    async fetchDetail(id) {
      const res = await MaterialRequisitionService.getById(id);
      return get(res, 'data.data', {}) || {};
    },
    errMsg(e, fallback) {
      return get(e, 'response.data.message') || fallback;
    },
  },
};
</script>

<style scoped>
.mrq-title {
  font-weight: 600;
  color: #3f4254;
}
.mrq-label {
  font-size: 0.85rem;
  color: #7e8299;
  margin-bottom: 2px;
}
.mrq-table th,
.mrq-line-table th {
  background: #f3f6f9;
  font-size: 12px;
  vertical-align: middle;
}
.mrq-table td,
.mrq-line-table td {
  vertical-align: middle;
  font-size: 13px;
}
.mrq-code {
  font-family: monospace;
  font-weight: 600;
}

/* Bảng dòng vật tư khi tạo phiếu: KHÔNG cắt dropdown tìm vật tư (vue-multiselect popup
   tràn ra ngoài cell). Modal size=xl nên không cần scroll ngang. */
.mrq-create-lines {
  overflow: visible;
}
.mrq-create-lines .mrq-line-table td {
  overflow: visible;
}

/* Option trong dropdown tìm vật tư: 2 dòng (tên + mã/tồn), tự xuống dòng, không bị cắt. */
.mrq-opt {
  display: flex;
  flex-direction: column;
  white-space: normal;
  line-height: 1.35;
}
.mrq-opt-name {
  font-weight: 500;
}
.mrq-opt-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #b5b5c3;
}
.mrq-opt-code {
  font-family: monospace;
}
.mrq-opt-stock {
  font-weight: 500;
}
/* Khi option đang được highlight (nền xanh): chữ phụ vẫn đọc được. */
.mrq-product-select
  ::v-deep
  .multiselect__option--highlight
  .mrq-opt-meta {
  color: rgba(255, 255, 255, 0.85);
}
.mrq-product-select ::v-deep .multiselect__content-wrapper {
  width: auto;
  min-width: 100%;
}

/* Timeline lịch sử MRQ — mirror pattern .so-timeline của Detail.vue (CSS scoped không lan
   nên copy lại). */
.mrq-timeline {
  list-style: none;
  margin: 0;
  padding: 0 0 0 8px;
}
.mrq-timeline-item {
  position: relative;
  padding: 0 0 18px 24px;
  border-left: 2px solid #ebedf3;
}
.mrq-timeline-item:last-child {
  border-left-color: transparent;
  padding-bottom: 0;
}
.mrq-timeline-dot {
  position: absolute;
  left: -7px;
  top: 2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #3699ff;
  border: 2px solid #fff;
}
.mrq-timeline-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.mrq-timeline-action {
  color: #3f4254;
}
.mrq-timeline-time {
  font-size: 0.8rem;
  color: #b5b5c3;
}
.mrq-timeline-meta {
  font-size: 0.85rem;
  color: #7e8299;
  margin-top: 2px;
}
.mrq-timeline-note {
  margin-top: 4px;
  font-size: 0.85rem;
  color: #3f4254;
  font-style: italic;
}
</style>
